import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  HiOutlineHome, 
  HiOutlineSwatch, 
  HiOutlineGlobeAlt, 
  HiOutlineBell, 
  HiOutlineShieldCheck,
  HiOutlineCheck
} from "react-icons/hi2";
import { toast } from "react-toastify";
import { useTheme } from "../contexts/ThemeContext";
import { usePermissions } from "../hooks/usePermissions";
import { settingsService } from "../services";
import "./GeneralSettings.css";

const GeneralSettings = () => {
  const { theme, toggleTheme } = useTheme();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission("Admin.Manage");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    theme: theme || 'dark',
    accentColor: "#6366F1",
    timezone: "UTC",
    dateFormat: "DD/MM/YYYY",
    numberFormat: "en-US",
    emailDigest: "daily",
    notifPrefs: {
      "Work Orders": true,
      "CAPA Due": true,
      "Safety Alerts": true,
      "PM Tasks": true,
      "Incidents": true,
      "Hazard Reports": true
    },
    // Admin Only
    companyName: "",
    defaultTimezone: "UTC",
    defaultLanguage: "en"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await settingsService.getGeneralSettings();
        if (data) {
          setPrefs(prev => ({
            ...prev,
            ...data,
            notifPrefs: { ...prev.notifPrefs, ...(data.notifPrefs || {}) }
          }));
        }
      } catch (err) {
        console.error("Failed to load settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsService.updateGeneralSettings(prefs);
      toast.success("Preferences saved successfully!");
    } catch (err) {
      toast.error("Failed to save settings. Please try again.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPrefs(prev => ({ ...prev, [field]: value }));
  };

  const updateNotifPref = (cat, checked) => {
    setPrefs(prev => ({
      ...prev,
      notifPrefs: { ...prev.notifPrefs, [cat]: checked }
    }));
  };

  const timezones = Intl.supportedValuesOf('timeZone');

  if (loading) {
    return <div className="settings-loading">Loading preferences...</div>;
  }

  return (
    <div className="general-settings-container">
      <nav className="breadcrumb">
        <Link to="/home" className="breadcrumb-item"><HiOutlineHome /> Home</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">General Settings</span>
      </nav>

      <div className="settings-header">
        <h1>General Settings</h1>
        <p className="subtitle">Manage your personal preferences and workspace localization settings.</p>
      </div>

      <div className="settings-grid">
        <div className="settings-main">
          
          {/* Appearance Section */}
          <section className="settings-card">
            <div className="card-header">
              <HiOutlineSwatch className="card-icon" />
              <h3>Appearance</h3>
            </div>
            <div className="card-body">
              <div className="settings-field">
                <label>Theme</label>
                <div className="theme-toggle-wrap">
                  <select 
                    value={prefs.theme} 
                    onChange={(e) => {
                      const val = e.target.value;
                      updateField('theme', val);
                      // If toggling between light/dark, call global context
                      if (val !== theme) toggleTheme();
                    }}
                  >
                    <option value="light">Light Mode</option>
                    <option value="dark">Dark Mode</option>
                    <option value="system">Follow System</option>
                  </select>
                </div>
                <p className="field-hint">Choose between a light or dark interface.</p>
              </div>

              <div className="settings-field">
                <label>Accent Color</label>
                <div className="color-picker-wrap">
                  <input 
                    type="color" 
                    value={prefs.accentColor} 
                    onChange={(e) => updateField('accentColor', e.target.value)} 
                  />
                  <span className="color-hex">{prefs.accentColor}</span>
                </div>
                <p className="field-hint">Customizes buttons and active indicators.</p>
              </div>
            </div>
          </section>

          {/* Regional Section */}
          <section className="settings-card">
            <div className="card-header">
              <HiOutlineGlobeAlt className="card-icon" />
              <h3>Regional & Localization</h3>
            </div>
            <div className="card-body">
              <div className="settings-field">
                <label>Timezone</label>
                <select 
                  value={prefs.timezone} 
                  onChange={(e) => updateField('timezone', e.target.value)}
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div className="settings-field-row">
                <div className="settings-field">
                  <label>Date Format</label>
                  <select 
                    value={prefs.dateFormat} 
                    onChange={(e) => updateField('dateFormat', e.target.value)}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2026)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2026)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2026-12-31)</option>
                  </select>
                </div>

                <div className="settings-field">
                  <label>Number Format</label>
                  <select 
                    value={prefs.numberFormat} 
                    onChange={(e) => updateField('numberFormat', e.target.value)}
                  >
                    <option value="en-US">1,000.00 (US/UK)</option>
                    <option value="de-DE">1.000,00 (Europe)</option>
                    <option value="en-IN">1,00,000.00 (India)</option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* Notifications Section */}
          <section className="settings-card">
            <div className="card-header">
              <HiOutlineBell className="card-icon" />
              <h3>Notifications</h3>
            </div>
            <div className="card-body">
              <div className="settings-field">
                <label>Email Digest Frequency</label>
                <select 
                  value={prefs.emailDigest} 
                  onChange={(e) => updateField('emailDigest', e.target.value)}
                >
                  <option value="realtime">Real-time (Every action)</option>
                  <option value="daily">Daily summary</option>
                  <option value="weekly">Weekly digest</option>
                  <option value="off">Mute all emails</option>
                </select>
              </div>

              <div className="settings-field">
                <label>In-App Categories</label>
                <div className="toggles-grid">
                  {Object.keys(prefs.notifPrefs).map(cat => (
                    <label key={cat} className="toggle-item">
                      <input 
                        type="checkbox" 
                        checked={prefs.notifPrefs[cat]} 
                        onChange={(e) => updateNotifPref(cat, e.target.checked)}
                      />
                      <span>{cat}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Admin Section */}
          {isAdmin && (
            <section className="settings-card admin-border">
              <div className="card-header">
                <HiOutlineShieldCheck className="card-icon admin-color" />
                <h3>Tenant Settings <span className="admin-badge">ADMIN Only</span></h3>
              </div>
              <div className="card-body">
                <div className="settings-field">
                  <label>Company Display Name</label>
                  <input 
                    type="text" 
                    value={prefs.companyName} 
                    onChange={(e) => updateField('companyName', e.target.value)}
                    placeholder="MaX Global Corp" 
                  />
                  <p className="field-hint">Changes the branding text in sidebars and reports.</p>
                </div>

                <div className="settings-field-row">
                  <div className="settings-field">
                    <label>Default Workspace Timezone</label>
                    <select 
                      value={prefs.defaultTimezone} 
                      onChange={(e) => updateField('defaultTimezone', e.target.value)}
                    >
                      {timezones.map(tz => (
                        <option key={`def-${tz}`} value={tz}>{tz}</option>
                      ))}
                    </select>
                  </div>

                  <div className="settings-field">
                    <label>Default System Language</label>
                    <select 
                      value={prefs.defaultLanguage} 
                      onChange={(e) => updateField('defaultLanguage', e.target.value)}
                    >
                      <option value="en">English (US)</option>
                      <option value="hi">Hindi (India)</option>
                      <option value="es">Spanish (Global)</option>
                      <option value="zh">Chinese (Simplified)</option>
                    </select>
                  </div>
                </div>
              </div>
            </section>
          )}

          <div className="settings-actions">
            <button 
              className="btn-save-settings" 
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Updating..." : (
                <>
                  <HiOutlineCheck /> Save General Settings
                </>
              )}
            </button>
          </div>
        </div>

        <div className="settings-sidebar">
          <div className="help-card">
            <h4>Need Help?</h4>
            <p>Localization settings affect how dates, currency, and times are displayed throughout the MaX platform.</p>
            <Link to="/home/opl/faq" className="help-link">View Documentation</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralSettings;
